import React, { useState, useEffect } from 'react';
import {
    FiSave, FiPlus, FiTrash2, FiMessageSquare, FiBell, FiZap,
    FiBarChart2, FiRefreshCw,
} from 'react-icons/fi';
import { Sector } from '../services/SectorService';
import { useSector } from '../contexts/SectorContext';
import {
    getFollowUpSettings,
    saveFollowUpSettings,
    getFollowUpStats,
    suggestFollowUpRule,
    FollowUpRuleDTO,
    FollowUpStats,
    InactivityUnit,
} from '../services/FollowUpService';
import { optimizeText } from '../services/TextOptimizationService';
import { apiErrorMessage } from '../utils/apiErrorMessage';
import SessionService from '../services/SessionService';
import Toast, { ToastContainer } from '../components/Toast';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { AgentPickerField } from '../components/AgentPickerModal';
import { getColumns, Column as CrmColumn } from '../services/ColumnService';
import { listUnifiedAgents } from '../services/UnifiedAIAgentService';
import { formatInTimezone, timezoneDisplayLabel } from '../utils/dateTimeUtils';
import './FollowUpPage.css';

interface ToastItem {
    id: number;
    message: string;
    type: 'success' | 'error' | 'info';
}

interface DraftRule {
    key: string;
    id: string;
    name: string;
    prompt: string;
    content: string;
    inactivityValue: number;
    inactivityUnit: InactivityUnit;
    dailyLeadCap: number;
    aiAgentId: number | null;
    aiAgentName: string;
    enabled: boolean;
    priority: number;
    triggerType: string;
    triggerStage: string | null;
    triggerColumnId: number | null;
    appointmentTypeName: string | null;
    appointmentOutcome: string | null;
    cooldownHours: number;
    afterHours: number | null;
}

// Janela retroativa não é mais configurável na UI — fixa em 30 dias.
const DEFAULT_RETROACTIVE_DAYS = 30;

const nextKey = (() => {
    let counter = 0;
    return () => `r-${Date.now()}-${counter++}`;
})();

const newRuleId = (): string => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    return `rule-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const toMinutes = (value: number, unit: InactivityUnit): number => {
    if (!value || value <= 0) return 0;
    if (unit === 'hours') return value * 60;
    if (unit === 'days') return value * 60 * 24;
    return value;
};

const clampInt = (raw: string, min: number): number => {
    const n = parseInt(raw, 10);
    if (isNaN(n)) return min;
    return n < min ? min : n;
};

const toDrafts = (rules: FollowUpRuleDTO[], agentNames: Map<number, string>): DraftRule[] =>
    (rules ?? []).map(r => ({
        key: nextKey(),
        id: r.id || newRuleId(),
        name: r.name ?? '',
        prompt: r.prompt ?? '',
        content: r.content ?? '',
        inactivityValue: r.inactivityValue ?? 0,
        inactivityUnit: (r.inactivityUnit as InactivityUnit) ?? 'minutes',
        dailyLeadCap: r.dailyLeadCap ?? 0,
        aiAgentId: r.aiAgentId ?? null,
        aiAgentName: r.aiAgentId ? (agentNames.get(r.aiAgentId) ?? '') : '',
        enabled: r.enabled !== false,
        priority: r.priority ?? 100,
        triggerType: r.triggerType || 'inactivity',
        triggerStage: r.triggerStage ?? null,
        triggerColumnId: r.triggerColumnId ?? null,
        appointmentTypeName: r.appointmentTypeName ?? null,
        appointmentOutcome: r.appointmentOutcome ?? null,
        cooldownHours: r.cooldownHours ?? 48,
        afterHours: r.afterHours ?? null,
    }));

const FollowUpPage: React.FC = () => {
    const { selectedSectorId, sectors } = useSector();
    const currentSector: Sector | null = selectedSectorId
        ? sectors.find(s => s.id === selectedSectorId) ?? null
        : null;

    const [enabled, setEnabled] = useState(false);
    const [rules, setRules] = useState<DraftRule[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [optimizingKey, setOptimizingKey] = useState<string | null>(null);
    const [suggestText, setSuggestText] = useState('');
    const [suggesting, setSuggesting] = useState(false);
    const [toasts, setToasts] = useState<ToastItem[]>([]);

    // Dashboard
    const [stats, setStats] = useState<FollowUpStats | null>(null);
    const [statsDays, setStatsDays] = useState(30);
    const [statsLoading, setStatsLoading] = useState(false);
    const [effectiveTimezone, setEffectiveTimezone] = useState('UTC');
    const [crmColumns, setCrmColumns] = useState<CrmColumn[]>([]);

    useEffect(() => {
        if (!currentSector?.id) {
            setCrmColumns([]);
            return;
        }
        getColumns().then(setCrmColumns).catch(() => setCrmColumns([]));
    }, [currentSector?.id]);

    const addToast = (message: string, type: 'success' | 'error' | 'info') => {
        const id = Date.now();
        setToasts(prev => [...prev, { id, message, type }]);
    };

    const removeToast = (id: number) => {
        setToasts(prev => prev.filter(t => t.id !== id));
    };

    useEffect(() => {
        if (currentSector?.id) {
            loadSettings(currentSector.id);
            loadStats(currentSector.id, statsDays);
        } else {
            setEnabled(false);
            setRules([]);
            setStats(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentSector?.id]);

    const loadSettings = async (sectorId: number) => {
        setIsLoading(true);
        try {
            const token = SessionService.getToken();
            if (!token) return;
            const [data, agents] = await Promise.all([
                getFollowUpSettings(sectorId, token),
                listUnifiedAgents(sectorId, token, 'followup').catch(() => []),
            ]);
            const agentNames = new Map(agents.map(a => [a.id, a.name]));
            setEnabled(Boolean(data.enabled));
            setEffectiveTimezone(data.effectiveTimezone || 'UTC');
            setRules(toDrafts(data.rules, agentNames));
        } catch (error) {
            console.error('Error loading follow-up settings:', error);
            setEnabled(false);
            setRules([]);
        } finally {
            setIsLoading(false);
        }
    };

    const loadStats = async (sectorId: number, days: number) => {
        setStatsLoading(true);
        try {
            const token = SessionService.getToken();
            if (!token) return;
            const data = await getFollowUpStats(sectorId, days, token);
            setStats(data);
            if (data.effectiveTimezone) setEffectiveTimezone(data.effectiveTimezone);
        } catch (error) {
            console.error('Error loading follow-up stats:', error);
            setStats(null);
        } finally {
            setStatsLoading(false);
        }
    };

    const handleChangeStatsDays = (days: number) => {
        setStatsDays(days);
        if (currentSector?.id) loadStats(currentSector.id, days);
    };

    const handleAddRule = () => {
        setRules(prev => [
            ...prev,
            {
                key: nextKey(),
                id: newRuleId(),
                name: '',
                prompt: '',
                content: '',
                inactivityValue: 30,
                inactivityUnit: 'minutes',
                dailyLeadCap: 0,
                aiAgentId: null,
                aiAgentName: '',
                enabled: true,
                priority: 100,
                triggerType: 'inactivity',
                triggerStage: null,
                triggerColumnId: null,
                appointmentTypeName: null,
                appointmentOutcome: null,
                cooldownHours: 48,
                afterHours: null,
            },
        ]);
    };

    const handleRemoveRule = (key: string) => {
        setRules(prev => prev.filter(r => r.key !== key));
    };

    const updateRule = (key: string, patch: Partial<DraftRule>) => {
        setRules(prev => prev.map(r => (r.key === key ? { ...r, ...patch } : r)));
    };

    const handleOptimizePrompt = async (key: string) => {
        if (!currentSector?.id) {
            addToast('Nenhuma conexão selecionada', 'error');
            return;
        }
        const rule = rules.find(r => r.key === key);
        if (!rule) return;

        setOptimizingKey(key);
        try {
            const optimized = await optimizeText(currentSector.id, 'followup_prompt', rule.prompt, undefined, undefined, 'followup');
            updateRule(key, { prompt: optimized });
            addToast('Prompt reescrito pela IA', 'success');
        } catch (error: any) {
            console.error('Error optimizing prompt:', error);
            addToast(apiErrorMessage(error, 'Não foi possível otimizar o prompt'), 'error');
        } finally {
            setOptimizingKey(null);
        }
    };

    const handleSuggestRule = async () => {
        if (!currentSector?.id || !suggestText.trim()) {
            addToast('Descreva a regra em linguagem natural', 'error');
            return;
        }
        setSuggesting(true);
        try {
            const token = SessionService.getToken();
            if (!token) return;
            const draft = await suggestFollowUpRule(currentSector.id, suggestText.trim(), token);
            setRules(prev => [
                ...prev,
                {
                    key: nextKey(),
                    id: newRuleId(),
                    name: draft.name || 'Regra sugerida',
                    prompt: draft.prompt || suggestText.trim(),
                    content: draft.content || '',
                    inactivityValue: draft.inactivityValue ?? draft.afterHours ?? 48,
                    inactivityUnit: (draft.inactivityUnit as InactivityUnit) || 'hours',
                    dailyLeadCap: draft.dailyLeadCap ?? 0,
                    aiAgentId: draft.aiAgentId ?? null,
                    aiAgentName: '',
                    enabled: false, // draft nunca ativa até Save
                    priority: draft.priority ?? 100,
                    triggerType: draft.triggerType || 'inactivity',
                    triggerStage: draft.triggerStage ?? null,
                    triggerColumnId: null,
                    appointmentTypeName: draft.appointmentTypeName ?? null,
                    appointmentOutcome: draft.appointmentOutcome ?? null,
                    cooldownHours: draft.cooldownHours ?? 48,
                    afterHours: draft.afterHours ?? null,
                },
            ]);
            setSuggestText('');
            addToast('Rascunho adicionado (desabilitado). Revise e salve para ativar.', 'success');
        } catch (error: any) {
            // 422 RULE_NOT_UNDERSTOOD: a IA não entendeu com segurança; a mensagem do servidor diz como descrever.
            const serverMessage = typeof error?.response?.data?.message === 'string' ? error.response.data.message : null;
            addToast(serverMessage || 'Não consegui sugerir a regra agora. Tente de novo.', serverMessage ? 'info' : 'error');
        } finally {
            setSuggesting(false);
        }
    };

    const handleSave = async () => {
        if (!currentSector?.id) {
            addToast('Nenhuma conexão selecionada', 'error');
            return;
        }

        const payloadRules: FollowUpRuleDTO[] = rules
            .map(r => {
                const inactivityValue = r.inactivityValue < 0 ? 0 : r.inactivityValue;
                const unit = r.inactivityUnit || 'minutes';
                return {
                    id: r.id,
                    name: r.name.trim() || 'Regra sem nome',
                    prompt: r.prompt.trim(),
                    content: r.content.trim(),
                    inactivityValue,
                    inactivityUnit: unit,
                    minInactivityMinutes: toMinutes(inactivityValue, unit),
                    retroactiveDays: DEFAULT_RETROACTIVE_DAYS,
                    dailyLeadCap: r.dailyLeadCap < 0 ? 0 : r.dailyLeadCap,
                    aiAgentId: r.aiAgentId,
                    enabled: r.enabled !== false,
                    priority: r.priority ?? 100,
                    triggerType: r.triggerType || 'inactivity',
                    triggerStage: r.triggerStage,
                    triggerColumnId: r.triggerType === 'column' ? r.triggerColumnId : null,
                    appointmentTypeName: r.appointmentTypeName,
                    appointmentOutcome: r.appointmentOutcome,
                    afterHours: r.triggerType === 'appointment_outcome'
                        ? waitHours(inactivityValue, unit, r.afterHours)
                        : (r.afterHours ?? (unit === 'hours' ? inactivityValue : null)),
                    cooldownHours: r.cooldownHours ?? 48,
                };
            })
            .filter(r => r.prompt.length > 0 && r.content.length > 0);

        if (enabled && payloadRules.length === 0) {
            addToast('Adicione pelo menos uma regra com prompt e mensagem para ativar o follow-up', 'error');
            return;
        }

        if (payloadRules.some(r => r.triggerType === 'column' && !r.triggerColumnId)) {
            addToast('Escolha a coluna do CRM nas regras com gatilho "Contatos numa coluna"', 'error');
            return;
        }

        if (enabled && payloadRules.some(r => !r.aiAgentId || r.aiAgentId <= 0)) {
            addToast('Cada regra precisa de um funcionário de follow-up (crie um em Funcionários se a lista estiver vazia)', 'error');
            return;
        }

        setIsSaving(true);
        try {
            const token = SessionService.getToken();
            if (!token) return;
            const saved = await saveFollowUpSettings(
                currentSector.id,
                { enabled, rules: payloadRules },
                token
            );
            const agents = await listUnifiedAgents(currentSector.id, token, 'followup').catch(() => []);
            const agentNames = new Map(agents.map(a => [a.id, a.name]));
            setEnabled(Boolean(saved.enabled));
            if (saved.effectiveTimezone) setEffectiveTimezone(saved.effectiveTimezone);
            setRules(toDrafts(saved.rules, agentNames));
            addToast('Follow-up salvo.', 'success');
        } catch (error) {
            console.error('Error saving follow-up settings:', error);
            addToast('Não foi possível salvar as configurações de follow-up', 'error');
        } finally {
            setIsSaving(false);
        }
    };

    if (!currentSector) {
        return (
            <div className="followup-wrapper">
                <div className="followup-page">
                    <div className="followup-header">
                        <div className="followup-header-content">
                            <h1>Follow-up</h1>
                            <p className="followup-header-description">
                                Configure as regras de follow-up dos seus leads
                            </p>
                        </div>
                    </div>
                    <div className="followup-no-sector">
                        <h3>Nenhuma conexão selecionada</h3>
                        <p>Selecione uma conexão para configurar as regras de follow-up</p>
                    </div>
                </div>
            </div>
        );
    }

    const responseRatePct = stats && stats.sent > 0
        ? Math.round(stats.responseRate * 100)
        : 0;

    return (
        <div className="followup-wrapper">
            <div className="followup-page">
                <ToastContainer>
                    {toasts.map(toast => (
                        <Toast
                            key={toast.id}
                            message={toast.message}
                            type={toast.type}
                            onClose={() => removeToast(toast.id)}
                        />
                    ))}
                </ToastContainer>

                <div className="followup-header">
                    <div className="followup-header-content">
                        <h1>Follow-up</h1>
                        <p className="followup-header-description">
                            Cada regra tem um prompt que diz à IA quando disparar. Tempo, alcance e limites ficam
                            nos campos abaixo, nunca dentro do prompt. Janelas de data e limites diários usam o fuso
                            horário do seu perfil ({timezoneDisplayLabel(effectiveTimezone)}).
                        </p>
                    </div>
                    <div className="followup-connection-badge">
                        <FiMessageSquare />
                        {currentSector.name}
                    </div>
                </div>

                <div className="followup-content">
                    {isLoading ? (
                        <div className="followup-loading">
                            <LoadingSpinner size="lg" color="primary" />
                            <span>Carregando configurações...</span>
                        </div>
                    ) : (
                        <div className="followup-grid">
                            {/* ---- Activation ---- */}
                            <div className="followup-card">
                                <div className="followup-card-header">
                                    <div className="followup-card-icon">
                                        <FiBell size={18} />
                                    </div>
                                    <h3 className="followup-card-title">Ativação</h3>
                                </div>
                                <div className="followup-card-content">
                                    <div className="followup-toggle-section">
                                        <div className="followup-toggle-info">
                                            <h4>Ativar follow-up</h4>
                                            <p>
                                                Quando ativo, o sistema verifica seus leads a cada 15 minutos e pergunta à IA
                                                se alguma regra se aplica, respeitando o tempo, o alcance e o limite diário de cada regra.
                                            </p>
                                        </div>
                                        <label className="followup-toggle-switch">
                                            <input
                                                type="checkbox"
                                                checked={enabled}
                                                onChange={(e) => setEnabled(e.target.checked)}
                                            />
                                            <span className="followup-toggle-slider"></span>
                                        </label>
                                    </div>
                                </div>
                            </div>

                            {/* ---- Metrics ---- */}
                            <div className="followup-card">
                                <div className="followup-card-header">
                                    <div className="followup-card-icon">
                                        <FiBarChart2 size={18} />
                                    </div>
                                    <h3 className="followup-card-title">Métricas</h3>
                                    <div className="followup-dash-actions">
                                        <select
                                            className="followup-delay-unit"
                                            value={statsDays}
                                            onChange={(e) => handleChangeStatsDays(parseInt(e.target.value, 10))}
                                        >
                                            <option value={7}>Últimos 7 dias</option>
                                            <option value={30}>Últimos 30 dias</option>
                                            <option value={90}>Últimos 90 dias</option>
                                        </select>
                                        <button
                                            type="button"
                                            className="followup-secondary-btn"
                                            onClick={() => currentSector?.id && loadStats(currentSector.id, statsDays)}
                                            disabled={statsLoading}
                                            title="Atualizar"
                                        >
                                            <FiRefreshCw size={14} />
                                            Atualizar
                                        </button>
                                    </div>
                                </div>
                                <div className="followup-card-content">
                                    {statsLoading ? (
                                        <div className="followup-loading" style={{ padding: 24 }}>
                                            <LoadingSpinner size="md" color="primary" />
                                        </div>
                                    ) : (
                                        <>
                                            <div className="followup-stats-grid">
                                                <div className="followup-stat">
                                                    <span className="followup-stat-value">{stats?.sent ?? 0}</span>
                                                    <span className="followup-stat-label">Follow-ups enviados</span>
                                                </div>
                                                <div className="followup-stat">
                                                    <span className="followup-stat-value">{stats?.responded ?? 0}</span>
                                                    <span className="followup-stat-label">Responderam depois</span>
                                                </div>
                                                <div className="followup-stat">
                                                    <span className="followup-stat-value">{responseRatePct}%</span>
                                                    <span className="followup-stat-label">Taxa de resposta</span>
                                                </div>
                                                <div className="followup-stat">
                                                    <span className="followup-stat-value">{stats?.errors ?? 0}</span>
                                                    <span className="followup-stat-label">Erros</span>
                                                </div>
                                            </div>

                                            {stats?.lastRunAt && (
                                                <p className="followup-hint">
                                                    Última execução: {formatInTimezone(stats.lastRunAt, effectiveTimezone)} ({timezoneDisplayLabel(effectiveTimezone)}).
                                                </p>
                                            )}

                                            {stats && stats.byRule.length > 0 && (
                                                <table className="followup-stat-table">
                                                    <thead>
                                                        <tr>
                                                            <th>Regra</th>
                                                            <th>Enviados</th>
                                                            <th>Respostas</th>
                                                            <th>Taxa</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {stats.byRule.map(r => (
                                                            <tr key={r.ruleId}>
                                                                <td>{r.name || 'Regra sem nome'}</td>
                                                                <td>{r.sent}</td>
                                                                <td>{r.responded}</td>
                                                                <td>{r.sent > 0 ? Math.round(r.responseRate * 100) : 0}%</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>

                            {/* ---- Rules ---- */}
                            <div className="followup-card">
                                <div className="followup-card-header">
                                    <div className="followup-card-icon">
                                        <FiZap size={18} />
                                    </div>
                                    <h3 className="followup-card-title">Regras</h3>
                                </div>
                                <div className="followup-card-content">
                                    <p className="followup-hint" style={{ marginTop: 0, marginBottom: 16 }}>
                                        O <strong>prompt</strong> é contexto legado. A verificação usa os campos estruturados
                                        (inatividade, estágio, resultado da reunião, intervalo mínimo). A sugestão da IA só cria rascunhos, nunca ativa sozinha.
                                    </p>

                                    <div className="followup-param" style={{ marginBottom: 16 }}>
                                        <label className="followup-param-label">Sugerir regra (rascunho da IA)</label>
                                        <div className="followup-param-inline" style={{ gap: 8, flexWrap: 'wrap' }}>
                                            <input
                                                type="text"
                                                className="followup-delay-input"
                                                style={{ flex: 1, minWidth: 220 }}
                                                placeholder="ex: follow-up após 48h sem resposta em proposta"
                                                value={suggestText}
                                                onChange={(e) => setSuggestText(e.target.value)}
                                            />
                                            <button
                                                type="button"
                                                className="followup-add-btn"
                                                disabled={suggesting || !suggestText.trim()}
                                                onClick={() => void handleSuggestRule()}
                                            >
                                                {suggesting ? '…' : 'Sugerir'}
                                            </button>
                                        </div>
                                    </div>

                                    {rules.length === 0 ? (
                                        <div className="followup-empty">
                                            Nenhuma regra ainda. Clique abaixo para adicionar a primeira.
                                        </div>
                                    ) : (
                                        <div className="followup-messages-list">
                                            {rules.map((r, idx) => (
                                                <div key={r.key} className="followup-message-item">
                                                    <div className="followup-message-row" style={{ flexWrap: 'wrap', gap: 8 }}>
                                                        <span className="followup-message-order">{idx + 1}</span>
                                                        <input
                                                            type="text"
                                                            className="followup-rule-name"
                                                            placeholder="Nome da regra (ex.: Lead pediu para aguardar)"
                                                            value={r.name}
                                                            onChange={(e) => updateRule(r.key, { name: e.target.value })}
                                                            style={{ flex: 1, minWidth: 200 }}
                                                        />
                                                        <button
                                                            type="button"
                                                            className="followup-remove-btn"
                                                            onClick={() => handleRemoveRule(r.key)}
                                                            aria-label="Remover regra"
                                                        >
                                                            <FiTrash2 size={14} />
                                                            Remover
                                                        </button>
                                                    </div>

                                                    <label className="followup-field-label" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                                        <input
                                                            type="checkbox"
                                                            checked={r.enabled}
                                                            onChange={(e) => updateRule(r.key, { enabled: e.target.checked })}
                                                        />
                                                        Regra ativa
                                                    </label>
                                                    {r.triggerType === 'appointment_outcome' ? (
                                                        <p className="followup-hint">
                                                            Gatilho: {r.appointmentTypeName ? `reunião de ${r.appointmentTypeName}` : 'qualquer reunião'}
                                                            {' · '}{appointmentOutcomeLabel(r.appointmentOutcome)}
                                                            {' · '}{r.afterHours ?? 48}h após o fim. O rascunho fica desligado até você marcar Regra ativa e salvar.
                                                        </p>
                                                    ) : r.triggerType === 'stage' && r.triggerStage ? (
                                                        <p className="followup-hint">Gatilho: estágio {r.triggerStage}.</p>
                                                    ) : null}

                                                    {(r.triggerType === 'inactivity' || r.triggerType === 'column') && (
                                                        <div className="followup-param">
                                                            <label className="followup-param-label">Quem recebe</label>
                                                            <div className="followup-param-inline">
                                                                <select
                                                                    className="followup-delay-unit"
                                                                    value={r.triggerType}
                                                                    onChange={(e) => updateRule(r.key, {
                                                                        triggerType: e.target.value,
                                                                        triggerColumnId: e.target.value === 'column' ? r.triggerColumnId : null,
                                                                    })}
                                                                >
                                                                    <option value="inactivity">Qualquer contato sem resposta</option>
                                                                    <option value="column">Contatos numa coluna do CRM</option>
                                                                </select>
                                                                {r.triggerType === 'column' && (
                                                                    <select
                                                                        className="followup-delay-unit"
                                                                        value={r.triggerColumnId ?? ''}
                                                                        onChange={(e) => updateRule(r.key, {
                                                                            triggerColumnId: e.target.value ? Number(e.target.value) : null,
                                                                        })}
                                                                    >
                                                                        <option value="">Escolha a coluna…</option>
                                                                        {crmColumns.map(c => (
                                                                            <option key={c.id} value={c.id}>{c.name}</option>
                                                                        ))}
                                                                    </select>
                                                                )}
                                                            </div>
                                                            <span className="followup-param-hint">
                                                                {r.triggerType === 'column'
                                                                    ? 'Ex.: coluna "Faltou à reunião" + 3 dias sem resposta. O agente desta regra redige e a mensagem aparece em Hoje › Suas decisões.'
                                                                    : 'Todo contato que ficar sem responder pelo tempo abaixo.'}
                                                            </span>
                                                        </div>
                                                    )}

                                                    <div className="followup-field-label-row">
                                                        <label className="followup-field-label">Prompt (critério de decisão da IA)</label>
                                                        <button
                                                            type="button"
                                                            className="followup-optimize-btn"
                                                            title="Otimizar com IA"
                                                            disabled={optimizingKey !== null}
                                                            onClick={() => handleOptimizePrompt(r.key)}
                                                        >
                                                            {optimizingKey === r.key ? <LoadingSpinner size="sm" color="white" /> : 'Otimizar'}
                                                        </button>
                                                    </div>
                                                    <textarea
                                                        className="followup-message-textarea"
                                                        placeholder="Só a regra que a IA segue. Ex.: 'Disparar se o lead demonstrou interesse mas não confirmou a compra.'"
                                                        value={r.prompt}
                                                        onChange={(e) => updateRule(r.key, { prompt: e.target.value })}
                                                        rows={4}
                                                    />

                                                    <label className="followup-field-label">Mensagem enviada ao lead</label>
                                                    <textarea
                                                        className="followup-message-textarea"
                                                        placeholder="Mensagem fixa enviada quando esta regra disparar."
                                                        value={r.content}
                                                        onChange={(e) => updateRule(r.key, { content: e.target.value })}
                                                        rows={3}
                                                    />

                                                    {currentSector?.id && (
                                                        <AgentPickerField
                                                            sectorId={currentSector.id}
                                                            capability="followup"
                                                            value={r.aiAgentId}
                                                            agentName={r.aiAgentName}
                                                            label="Funcionário de follow-up"
                                                            required
                                                            showManageLink
                                                            onChange={(id, name) => updateRule(r.key, { aiAgentId: id, aiAgentName: name })}
                                                        />
                                                    )}

                                                    {/* ---- Structured controls (not in the prompt) ---- */}
                                                    <div className="followup-params">
                                                        <div className="followup-param">
                                                            <label className="followup-param-label">
                                                                {r.triggerType === 'appointment_outcome' ? 'Depois do fim da reunião' : 'Sem resposta por'}
                                                            </label>
                                                            <div className="followup-param-inline">
                                                                <input
                                                                    type="number"
                                                                    min={0}
                                                                    className="followup-delay-input"
                                                                    value={r.inactivityValue}
                                                                    onChange={(e) => {
                                                                        const inactivityValue = clampInt(e.target.value, 0);
                                                                        updateRule(r.key, {
                                                                            inactivityValue,
                                                                            afterHours: r.triggerType === 'appointment_outcome'
                                                                                ? waitHours(inactivityValue, r.inactivityUnit, r.afterHours)
                                                                                : r.afterHours,
                                                                        });
                                                                    }}
                                                                />
                                                                <select
                                                                    className="followup-delay-unit"
                                                                    value={r.inactivityUnit}
                                                                    onChange={(e) => {
                                                                        const inactivityUnit = e.target.value as InactivityUnit;
                                                                        updateRule(r.key, {
                                                                            inactivityUnit,
                                                                            afterHours: r.triggerType === 'appointment_outcome'
                                                                                ? waitHours(r.inactivityValue, inactivityUnit, r.afterHours)
                                                                                : r.afterHours,
                                                                        });
                                                                    }}
                                                                >
                                                                    <option value="minutes">minutos</option>
                                                                    <option value="hours">horas</option>
                                                                    <option value="days">dias</option>
                                                                </select>
                                                            </div>
                                                            <span className="followup-param-hint">
                                                                {r.triggerType === 'appointment_outcome'
                                                                    ? 'Tempo de espera após o fim da reunião antes de propor o follow-up.'
                                                                    : 'Tempo mínimo desde a última mensagem do lead antes de disparar (duração, não depende da meia-noite).'}
                                                            </span>
                                                        </div>

                                                        <div className="followup-param">
                                                            <label className="followup-param-label">Limite diário de leads</label>
                                                            <div className="followup-param-inline">
                                                                <input
                                                                    type="number"
                                                                    min={0}
                                                                    className="followup-delay-input"
                                                                    value={r.dailyLeadCap}
                                                                    onChange={(e) => updateRule(r.key, { dailyLeadCap: clampInt(e.target.value, 0) })}
                                                                />
                                                                <span className="followup-delay-suffix">leads/dia</span>
                                                            </div>
                                                            <span className="followup-param-hint">
                                                                Máximo de leads que esta regra contata por dia, no fuso horário do seu perfil. 0 = sem limite.
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    <button type="button" className="followup-add-btn" onClick={handleAddRule}>
                                        <FiPlus size={16} />
                                        Adicionar regra
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <div className="followup-footer">
                    <button
                        className="followup-save-button"
                        onClick={handleSave}
                        disabled={isSaving || isLoading}
                    >
                        {isSaving ? (
                            <>
                                <LoadingSpinner size="sm" color="white" />
                                Salvando...
                            </>
                        ) : (
                            <>
                                <FiSave />
                                Salvar alterações
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

function waitHours(value: number, unit: InactivityUnit, current: number | null): number | null {
    if (!value || value <= 0) return current;
    if (unit === 'days') return value * 24;
    if (unit === 'hours') return value;
    return Math.max(1, Math.ceil(value / 60));
}

function appointmentOutcomeLabel(outcome: string | null): string {
    switch (outcome) {
        case 'no_show': return 'não compareceu';
        case 'no_decision': return 'sem decisão';
        case 'closed': return 'fechou';
        case 'lost': return 'perdeu';
        case 'occurred': return 'ocorreu';
        default: return outcome || 'resultado';
    }
}

export default FollowUpPage;
